import { hoursToMinutes } from '@acme/common'
import dayjs from 'dayjs'
import { useOutletContext } from 'react-router-dom'
import {
    Abono,
    Afastamento,
    CompensacaoFeriado,
    JornadaDiaria,
    JornadaDiariaQueryXor,
    JornadaMensal,
    JornadaMensalQueryXor,
    Registro,
    jornada,
} from 'service/api'
import { CompensacaoManual } from 'service/api/endpoints/compensacao-manual'
import { FaltaCadastrada } from 'service/api/endpoints/faltas-cadastradas'
import { useStore } from 'store'
import { useDigitacaoStore } from '../utils/store'
import { useDigitacaoContext } from './useDigitacaoContext'

export const useDigitacaoDataContext = () => {
    const context = useOutletContext<{
        data: JornadaMensal[] | JornadaDiaria[]
    }>()
    const registros = context.data?.map(jornada => jornada.registros).flat()
    return { registros, context }
}

export const invalidateDigitacaoQueries = async () =>
    Promise.all([
        jornada.mensal.invalidateQueries(),
        jornada.diaria.invalidateQueries(),
    ])

type RegistroPayload = (
    action: 'REGISTRO',
    payload: Registro,
    excluir?: boolean,
) => void
type AfastamentoPayload = (
    action: 'AFASTAMENTO',
    payload: Afastamento,
    excluir?: boolean,
) => void
type AbonoPayload = (action: 'ABONO', payload: Abono, excluir?: boolean) => void
type CompensacaoFeriadoPayload = (
    action: 'COMPENSACAO_FERIADO',
    payload: CompensacaoFeriado,
    excluir?: boolean,
) => void
type FaltaCadastradaPayload = (
    action: 'FALTA_CADASTRADA',
    payload: FaltaCadastrada,
    excluir?: boolean,
) => void
type CompensacaoManualPayload = (
    action: 'COMPENSACAO_MANUAL',
    payload: CompensacaoManual,
    excluir?: boolean,
) => void

type QueryData = RegistroPayload &
    AfastamentoPayload &
    AbonoPayload &
    CompensacaoFeriadoPayload &
    FaltaCadastradaPayload &
    CompensacaoManualPayload

export function useUpdateDigitacaoQueryData() {
    const funcionarioId = useStore(store => store.employeeId)
    const empresaId = useStore(store => store.companyId)
    const {
        view,
        periodo: [dataInicial, dataFinal],
    } = useDigitacaoContext()
    const organizacao = useDigitacaoStore(state => state.organizacao)

    if (view === 'mensal') {
        const setQueryData: QueryData = (action, payload, excluir) => {
            const filtro: JornadaMensalQueryXor | JornadaDiariaQueryXor = {
                funcionarioId,
                dataInicial,
                dataFinal,
                organizar: organizacao === 'jornada',
            }

            switch (action) {
                case 'REGISTRO':
                    jornada.mensal.setQueryData(
                        filtro,
                        updateRegistro(payload as Registro, excluir),
                    )
                    break
                case 'AFASTAMENTO':
                    jornada.mensal.setQueryData(
                        filtro,
                        updateAfastamento(payload as Afastamento, excluir),
                    )
                    break
                case 'ABONO':
                    jornada.mensal.setQueryData(
                        filtro,
                        updateAbono(payload as Abono, excluir),
                    )
                    break
                case 'COMPENSACAO_FERIADO':
                    jornada.mensal.setQueryData(
                        filtro,
                        updateCompensacaoFeriado(
                            payload as CompensacaoFeriado,
                            excluir,
                        ),
                    )
                    break
                case 'FALTA_CADASTRADA':
                    jornada.mensal.setQueryData(
                        filtro,
                        updateFaltaCadastrada(
                            payload as FaltaCadastrada,
                            excluir,
                        ),
                    )
                    break
                case 'COMPENSACAO_MANUAL':
                    jornada.mensal.setQueryData(
                        filtro,
                        updateCompensacaoManual(
                            payload as CompensacaoManual,
                            excluir,
                        ),
                    )
                    break
            }
        }

        return { setQueryData }
    } else {
        const setQueryData: QueryData = (action, payload, excluir) => {
            const filtro = {
                empresaId,
                dia: dataInicial,
            }

            switch (action) {
                case 'REGISTRO':
                    jornada.diaria.setQueryData(
                        filtro,
                        updateRegistro(payload as Registro, excluir),
                    )
                    break
                case 'AFASTAMENTO':
                    jornada.diaria.setQueryData(
                        filtro,
                        updateAfastamento(payload as Afastamento, excluir),
                    )
                    break
                case 'ABONO':
                    jornada.diaria.setQueryData(
                        filtro,
                        updateAbono(payload as Abono, excluir),
                    )
                    break
                case 'COMPENSACAO_FERIADO':
                    jornada.diaria.setQueryData(
                        filtro,
                        updateCompensacaoFeriado(
                            payload as CompensacaoFeriado,
                            excluir,
                        ),
                    )
                    break
                case 'FALTA_CADASTRADA':
                    jornada.diaria.setQueryData(
                        filtro,
                        updateFaltaCadastrada(
                            payload as FaltaCadastrada,
                            excluir,
                        ),
                    )
                    break
                case 'COMPENSACAO_MANUAL':
                    jornada.diaria.setQueryData(
                        filtro,
                        updateCompensacaoManual(
                            payload as CompensacaoManual,
                            excluir,
                        ),
                    )
                    break
            }
        }

        return { setQueryData }
    }
}

function updateRegistro<T extends JornadaDiaria | JornadaMensal>(
    input: Registro | Registro[],
    excluir?: boolean,
) {
    let funcionarioId: number

    return (oldData?: T[]): T[] => {
        if (!Array.isArray(input)) {
            funcionarioId = input.funcionarioId
            input = [input]
        } else funcionarioId = input[0].funcionarioId

        if (!oldData) return [] as T[]

        for (const registro of input) {
            registro.updatedAt = new Date()
            const dia = dayjs(registro.data).utc().format('YYYY-MM-DD')

            oldData = oldData.map(item => {
                const diario = 'id' in item

                if (diario) {
                    if (funcionarioId !== item.id) return item

                    const indexRegistro = item.registros.findIndex(
                        value => value.id === registro.id,
                    )
                    if (indexRegistro !== -1) {
                        if (excluir) {
                            item.registros.splice(indexRegistro, 1)
                        } else {
                            item.registros[indexRegistro] = registro
                        }
                    } else {
                        if (!excluir) {
                            item.registros = item.registros
                                .concat(registro)
                                .sort((a, b) => {
                                    const horaA = hoursToMinutes(a.hora)
                                    const horaB = hoursToMinutes(b.hora)
                                    return horaA - horaB
                                })
                        }
                    }
                } else if (item.dia === dia) {
                    const indexRegistro = item.registros.findIndex(
                        value => value.hora === registro.hora,
                    )
                    if (indexRegistro !== -1) {
                        if (excluir) {
                            item.registros.splice(indexRegistro, 1)
                        } else {
                            item.registros[indexRegistro] = registro
                        }
                    } else {
                        if (!excluir) {
                            item.registros.push(registro)
                            item.registros = item.registros.sort((a, b) => {
                                const horaA = hoursToMinutes(a.hora)
                                const horaB = hoursToMinutes(b.hora)
                                return horaA - horaB
                            })
                        }
                    }
                }

                return item
            }) as T[]
        }

        return oldData
    }
}

type AfastamentoRange = { dia: string; afastamento: Afastamento }

function updateAfastamento<T extends JornadaDiaria | JornadaMensal>(
    afastamento: Afastamento,
    excluir?: boolean,
) {
    const { funcionarioId, id } = afastamento

    return (oldData?: T[]): T[] => {
        if (!oldData) return [] as T[]

        const afastamentoRange: AfastamentoRange[] = []

        const diaInicio = dayjs.utc(afastamento.dataInicial)
        const diaFinal = dayjs.utc(afastamento.dataFinal)

        const diff = diaFinal.diff(diaInicio, 'days')

        for (let i = 0; i <= diff; i += 1) {
            const dia = diaInicio.add(i, 'days').format('YYYY-MM-DD')
            afastamentoRange.push({ dia, afastamento })
        }

        return oldData.map(item => {
            const diario = 'id' in item

            if (excluir && item.afastamento?.id === id) {
                item.afastamento = undefined
                return item
            }

            const afastamentoExiste = afastamentoRange.find(
                it => it.dia === item.dia,
            )

            if (afastamentoExiste) {
                if (diario && funcionarioId === item.id) {
                    item.afastamento = afastamento
                } else if (
                    !diario &&
                    dayjs
                        .utc(item.dia)
                        .isSameOrBefore(afastamento.dataFinal, 'day') &&
                    dayjs
                        .utc(item.dia)
                        .isSameOrAfter(afastamento.dataInicial, 'day')
                ) {
                    item.afastamento = afastamento
                }
            }

            return item
        })
    }
}

function updateAbono<T extends JornadaDiaria | JornadaMensal>(
    abono: Abono,
    excluir?: boolean,
) {
    const { funcionarioId, id: abonoId } = abono

    return (oldData?: T[]): T[] => {
        if (!oldData) return [] as T[]

        console.debug('updateAbono', abono, excluir)

        return oldData.map(item => {
            // Caso o abono tenha alterado o dia
            if (item.abono && item.abono.id === abonoId) {
                item.abono = undefined
            }

            const diario = 'id' in item

            if (diario && funcionarioId === item.id) {
                if (excluir) item.abono = undefined
                else item.abono = abono
            } else if (
                !diario &&
                dayjs(item.dia).utc().isSame(abono.data, 'day')
            ) {
                if (excluir) item.abono = undefined
                else item.abono = abono
            }

            return item
        })
    }
}

function updateFaltaCadastrada<T extends JornadaDiaria | JornadaMensal>(
    falta: FaltaCadastrada,
    excluir?: boolean,
) {
    const { funcionarioId } = falta

    return (oldData?: T[]): T[] => {
        if (!oldData) return [] as T[]

        console.debug('updateFaltaCadastrada', falta, excluir)

        return oldData.map(item => {
            const diario = 'id' in item

            if (diario && funcionarioId === item.id) {
                if (excluir) item.faltaCadastrada = undefined
                else item.faltaCadastrada = falta
            } else if (
                !diario &&
                dayjs(item.dia).utc().isSame(falta.data, 'day')
            ) {
                if (excluir) item.faltaCadastrada = undefined
                else item.faltaCadastrada = falta
            }

            return item
        })
    }
}

function updateCompensacaoFeriado<T extends JornadaDiaria | JornadaMensal>(
    compensacaoFeriado: CompensacaoFeriado,
    excluir?: boolean,
) {
    const { funcionarioId } = compensacaoFeriado

    return (oldData?: T[]): T[] => {
        if (!oldData) return [] as T[]

        return oldData.map(item => {
            const diario = 'id' in item

            if (diario && funcionarioId === item.id) {
                if (excluir) item.compensacao = undefined
                else item.compensacao = compensacaoFeriado
            } else if (
                !diario &&
                dayjs(item.dia)
                    .utc()
                    .isSame(compensacaoFeriado.dataCompensacao, 'day')
            ) {
                if (excluir) item.compensacao = undefined
                else item.compensacao = compensacaoFeriado
            }

            return item
        })
    }
}

function updateCompensacaoManual<T extends JornadaDiaria | JornadaMensal>(
    compensacaoManual: CompensacaoManual,
    excluir?: boolean,
) {
    return (oldData?: T[]): T[] => {
        if (!oldData) return [] as T[]

        return oldData.map(item => {
            // Verificar se a data principal corresponde ao dia atual
            const isOriginDay = dayjs(item.dia)
                .utc()
                .isSame(compensacaoManual.data, 'day')

            // Verificar se algum destino corresponde ao dia atual
            const isDestinyDay = compensacaoManual.Destinos?.some(destino =>
                dayjs(item.dia).utc().isSame(destino.data, 'day'),
            )

            if (isOriginDay || isDestinyDay) {
                if (excluir) item.compensacaoManual = undefined
                else item.compensacaoManual = compensacaoManual
            }

            return item
        })
    }
}
